import { useEffect, useState } from 'react';
import { Form, FormInstance } from 'antd';
import { useTranslation } from 'react-i18next';
import fieldActivityService from '../../../../services/field-activity/field-activity.service';
import { Vehicle, VehicleAssignmentType } from '../../../../types/vehicle.types';
import { handleApiError } from '../../../../utils/errorHandler';

interface UseAvailableVehiclesOptions {
  enabled: boolean;
  userId?: number;
  onlyPool?: boolean;
  excludeActivityId?: number;
}

/**
 * Aktivite formunda seçilen çalışan ve tarih aralığına göre kullanılabilir araçları getirir
 * (çalışana zimmetli araçlar + boştaki havuz araçları). Form'daki "dates" alanını izler;
 * seçili araç yeni listede yoksa temizlenir.
 */
export const useAvailableVehicles = (
  form: FormInstance,
  { enabled, userId, onlyPool = false, excludeActivityId }: UseAvailableVehiclesOptions
) => {
  const { t } = useTranslation();
  const dates = Form.useWatch('dates', form);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(false);

  // Aktivite oluşturma isteğiyle aynı formatta (ISO) gönderilir
  const startDate: string | undefined = dates?.[0] ? dates[0].toISOString() : undefined;
  const endDate: string | undefined = dates?.[1] ? dates[1].toISOString() : undefined;

  useEffect(() => {
    if (!enabled || !userId || !startDate) {
      setVehicles([]);
      return;
    }

    let cancelled = false;
    setLoading(true);

    fieldActivityService
      .getAvailableVehicles({ userId, startDate, endDate, excludeActivityId })
      .then((list) => {
        if (cancelled) return;
        const filtered = onlyPool
          ? list.filter((vehicle) => vehicle.assignmentType === VehicleAssignmentType.POOL)
          : list;
        setVehicles(filtered);

        const selectedVehicleId = form.getFieldValue('vehicleId');
        if (selectedVehicleId && !filtered.some((vehicle) => vehicle.id === selectedVehicleId)) {
          form.setFieldValue('vehicleId', undefined);
        }
      })
      .catch((error) => {
        if (cancelled) return;
        setVehicles([]);
        handleApiError(error, t);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, userId, startDate, endDate, onlyPool, excludeActivityId]);

  return { vehicles, loading, hasDates: !!startDate };
};

export const getVehicleOptionLabel = (vehicle: Vehicle) =>
  `${vehicle.name} - ${vehicle.plateNumber}${vehicle.assignmentType === VehicleAssignmentType.POOL ? ' (Havuz)' : ''}`;
